import React, { useEffect, useMemo, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { EnrollmentUpdateRow, RosterRow, ScheduleRosterDTO, TargetSchedule, UPDATE_TYPE, UpdateOptionsDTO } from "../../../../models/EnrollmentUpdateDTO";
import { cursoCorto } from "../../../../utils/cursoCorto";
import BuscadorHorarios, { DetalleHorario, FiltroCasillas, HorarioFila, filaDeDestino } from "../../../../components/horarios/buscadorHorarios";
import { useVeWappChat } from "../../../../components/whatsapp/whatsappAlumno";
import EnvioMasivo from "../../../../components/whatsapp/envioMasivo";
import S from "./strings";
import { TabProps, TYPE_LABELS, dinero, errorText, fechaCorta, nombreHorario, sessionDelta } from "./helpers";
import { PromptModal } from "./piezas";
import { HistorialLista } from "./historialTab";

interface Props extends TabProps { initialScheduleId?: number; onActualizar: (studentId: number, enrollmentId: number) => void; }

// Lo que se puede hacer con varias alumnas a la vez; Cambio a Otro Curso y Reingreso son de una en una.
const MOVER: number[] = [UPDATE_TYPE.SCHEDULE_CHANGE, UPDATE_TYPE.NEXT_BLOCK, UPDATE_TYPE.POSTPONED, UPDATE_TYPE.NO_SCHEDULE, UPDATE_TYPE.RETIRED];

const Modal: React.FC<{ titulo: string; onClose: () => void; children: React.ReactNode }> = ({ titulo, onClose, children }) => (
	<div className="wau-modal-fondo" role="dialog" aria-modal="true" onClick={onClose}>
		<div className="wau-modal ancho" onClick={(e) => e.stopPropagation()}>
			<h3>{titulo}</h3>
			{children}
			<div className="wau-acciones"><button type="button" className="wau-btn s" onClick={onClose}>{S.cancel}</button></div>
		</div>
	</div>
);

/** Actualizar, Un Horario: busca un horario, ve a sus alumnas y las actualiza una por una o en lote. */
const UnHorario: React.FC<Props> = ({ campusId, countryId, movil, canApplyNow, notify, changed, initialScheduleId, onActualizar }) => {
	const http = HttpManager.getInstance();
	const veWa = useVeWappChat();
	const [scheduleId, setScheduleId] = useState<number | null>(initialScheduleId ?? null);
	const [fila, setFila] = useState<HorarioFila | null>(null);
	const [buscando, setBuscando] = useState<boolean>(!initialScheduleId);
	const [roster, setRoster] = useState<ScheduleRosterDTO | null>(null);
	const [log, setLog] = useState<EnrollmentUpdateRow[]>([]);
	const [options, setOptions] = useState<UpdateOptionsDTO | null>(null);
	const [seg, setSeg] = useState<"alumnas" | "historial">("alumnas");
	const [pagoFiltro, setPagoFiltro] = useState<string[]>([]);
	const [asistFiltro, setAsistFiltro] = useState<string[]>([]);
	const [selected, setSelected] = useState<number[]>([]);
	const [typeId, setTypeId] = useState<number | null>(null);
	const [destino, setDestino] = useState<HorarioFila | null>(null);
	const [buscandoDestino, setBuscandoDestino] = useState<boolean>(true);
	const [notes, setNotes] = useState<{ [enrollmentId: number]: string }>({});
	const [modal, setModal] = useState<"close" | "merge" | "mentor" | null>(null);
	const [mentors, setMentors] = useState<{ id: number; name: string; enabled: boolean; openSchedules: number }[] | null>(null);
	const [mentorFiltro, setMentorFiltro] = useState<string>("");
	const [masivo, setMasivo] = useState<boolean>(false);
	const [mergeTo, setMergeTo] = useState<HorarioFila | null>(null);
	const [mergeReason, setMergeReason] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [tick, setTick] = useState<number>(0);

	useEffect(() => {
		if (!scheduleId) { setRoster(null); return; }
		let vigente = true;
		setRoster(null); setLog([]); setOptions(null); setSelected([]); setTypeId(null); setDestino(null); setBuscandoDestino(true); setNotes({});
		Promise.all([
			http.enrollmentUpdatesService.scheduleRoster(campusId, scheduleId),
			http.enrollmentUpdatesService.log(campusId, { scheduleId }).then((r) => r.data.data).catch(() => [] as EnrollmentUpdateRow[])
		]).then(([r, l]) => {
			if (!vigente) { return; }
			setRoster(r.data); setLog(l);
			// Los destinos y los tipos son los mismos para todo el grupo (mismo curso): se piden con la primera alumna libre.
			const primera = r.data.rows.find((x) => !x.pendingUpdate || x.pendingUpdate.typeId === null) || r.data.rows[0];
			if (primera) { http.enrollmentUpdatesService.options(campusId, primera.enrollmentId).then((o) => vigente && setOptions(o.data)).catch(() => { /* sin destinos */ }); }
		}).catch((e) => vigente && notify(errorText(e), false));
		return () => { vigente = false; };
	}, [campusId, scheduleId, tick]);

	const targets: TargetSchedule[] = useMemo(() => options?.targetSchedules || [], [options]);
	const soloIds = useMemo(() => targets.map((t) => t.id), [targets]);
	const complemento = useMemo(() => targets.map((t) => filaDeDestino(t, cursoCorto(roster?.schedule.courseName), campusId)), [targets, roster, campusId]);
	const detalle = useMemo(() => {
		const out: { [id: number]: DetalleHorario } = {};
		targets.forEach((t) => { out[t.id] = { cupo: t.limit || null, alumnos: t.enrolled, lleno: t.isFull, sesion: t.classesGiven + 1 }; });
		return out;
	}, [targets]);
	const target = targets.find((t) => t.id === destino?.id);

	const sch = roster?.schedule;
	const rows = roster?.rows || [];
	const libre = (r: RosterRow) => !r.pendingUpdate || r.pendingUpdate.typeId === null;
	const ultima = useMemo(() => {
		const mapa = new Map<number, EnrollmentUpdateRow>();
		log.forEach((u) => { if (u.statusId === 3 && !mapa.has(u.enrollmentId)) { mapa.set(u.enrollmentId, u); } });
		return mapa;
	}, [log]);
	const porUbicar = rows.filter((r) => r.pendingUpdate && r.pendingUpdate.typeId === null).length;

	const vistas = rows.filter((r) => {
		if (pagoFiltro.length && !pagoFiltro.includes(r.overdue > 0 ? "vencida" : "corriente")) { return false; }
		if (asistFiltro.length && !asistFiltro.includes(r.alerts.some((a) => a.code === "below_minimum") ? "baja" : "normal")) { return false; }
		return true;
	});
	const personas = rows.filter((r) => selected.includes(r.enrollmentId));
	const allowedTypes = MOVER.filter((id) => (options?.types || []).some((t) => t.id === id && t.allowed));
	const necesitaDestino = typeId === UPDATE_TYPE.SCHEDULE_CHANGE || typeId === UPDATE_TYPE.NEXT_BLOCK;
	const missing = personas.find((p) => !(notes[p.enrollmentId] || "").trim());
	const anyMore = !!target && personas.some((p) => sessionDelta(p.sessionsTaken, target.classesGiven).more > 0);
	const listo = personas.length > 0 && !!typeId && (!necesitaDestino || !!destino) && !missing && !busy;
	const puedeAplicar = listo && !anyMore && canApplyNow && (options?.isCaja || (options?.isAcademic && [1, 2, 5].includes(typeId || 0)));

	const recargar = () => setTick((t) => t + 1);
	const termina = (msg: string) => { notify(msg); setModal(null); recargar(); changed(); };
	const act = (fn: () => Promise<any>, msg: string) => {
		setBusy(true);
		fn().then(() => termina(msg)).catch((e) => notify(errorText(e), false)).finally(() => setBusy(false));
	};

	const mover = async (applyNow: boolean) => {
		if (!typeId) { return; }
		setBusy(true);
		let hechas = 0; let aplicadas = 0;
		try {
			// Las que ya traen un borrador del cierre del horario se definen sobre ese borrador; las demás se crean.
			for (const p of personas.filter((x) => x.pendingUpdate && x.pendingUpdate.typeId === null)) {
				const d = await http.enrollmentUpdatesService.setType(campusId, p.pendingUpdate!.id, { typeId, toScheduleId: necesitaDestino ? destino!.id : null, note: notes[p.enrollmentId].trim() });
				if (applyNow && d.data.canApply && !d.data.needsCashier && !(d.data.previewNow?.sessionsMore)) { await http.enrollmentUpdatesService.apply(campusId, p.pendingUpdate!.id, {}); aplicadas++; }
				hechas++;
			}
			const nuevas = personas.filter((x) => !x.pendingUpdate);
			if (nuevas.length) {
				const creada = await http.enrollmentUpdatesService.create(campusId, { applyNow, scheduleId: scheduleId, label: `${cursoCorto(sch?.courseName)}, ${fila?.horario || nombreHorario(sch?.name)}`,
					items: nuevas.map((p) => ({ enrollmentId: p.enrollmentId, typeId, note: notes[p.enrollmentId].trim(), toScheduleId: necesitaDestino ? destino!.id : null })) });
				hechas += nuevas.length; if (applyNow) { aplicadas += (creada.data.updates || []).filter((u) => u.statusId === 3).length; }
			}
			notify(applyNow && aplicadas === hechas ? S.appliedN(hechas) : S.sent(hechas));
			changed(); recargar();
		} catch (e) {
			notify(errorText(e), false);
			if (hechas) { changed(); recargar(); }
		} finally { setBusy(false); }
	};

	const abrirModal = (m: "close" | "merge" | "mentor") => {
		setModal(m);
		if (m === "mentor" && !mentors) {
			http.enrollmentUpdatesService.mentors(campusId).then((r) => setMentors(Array.isArray(r.data) ? r.data : [])).catch((e) => notify(errorText(e), false));
		}
	};

	const pagoPill = (r: RosterRow) => r.overdue > 0
		? <span className="wau-pill warn">{S.payOverdue(dinero(r.overdue, countryId))}</span> : <span className="wau-pill ok">{S.payOk}</span>;
	const asistencia = (r: RosterRow) => r.attendancePct == null ? "—" : `${Math.round(r.attendancePct)} %`;
	const avance = (r: RosterRow) => r.sessionsTotal ? `${r.sessionsTaken} / ${r.sessionsTotal}` : String(r.sessionsTaken);
	const ultimaTexto = (r: RosterRow) => {
		if (r.pendingUpdate && r.pendingUpdate.typeId !== null) { return <span className="wau-pill warn">{S.waiting}</span>; }
		const u = ultima.get(r.enrollmentId);
		return u ? <span className="wau-sec">{TYPE_LABELS[u.typeId || 0] || u.typeLabel}, {fechaCorta(u.eventAt || u.appliedAt)}</span> : <span className="wau-sec">—</span>;
	};
	const toggle = (r: RosterRow) => { if (libre(r)) { setSelected(selected.includes(r.enrollmentId) ? selected.filter((x) => x !== r.enrollmentId) : [...selected, r.enrollmentId]); } };
	const libres = vistas.filter(libre);
	const todas = libres.length > 0 && libres.every((r) => selected.includes(r.enrollmentId));
	const mentorLista = (mentors || []).filter((m) => m.enabled && (!mentorFiltro || m.name.toLowerCase().includes(mentorFiltro.toLowerCase())));
	const estadoPill = sch ? <span className={"wau-pill " + (sch.statusId === 4 ? "warn" : "flat")}>{sch.statusLabel}</span> : null;

	return (
		<div className="wau-pila">
			<div className="wau-caja wau-pila">
				{scheduleId && !buscando ? (
					<div className="wau-destino">
						<span><b>{fila?.horario || nombreHorario(sch?.name) || "…"}</b>{sch ? ", " + (sch.levelName || cursoCorto(sch.courseName)) : ""}</span>
						<button type="button" className="wau-btn s mini" onClick={() => setBuscando(true)}>{S.buscarOtro}</button>
					</div>
				) : (
					<BuscadorHorarios campusId={campusId} seleccionado={scheduleId} placeholder={S.pickSchedule} onElegir={(h) => { setScheduleId(h.id); setFila(h); setBuscando(false); setSeg("alumnas"); }} />
				)}
			</div>

			{scheduleId && !roster ? <div className="wau-caja wau-sec">{S.loading}</div> : null}

			{sch ? (
				<div className="wau-caja wau-pila" style={{ borderColor: "var(--wa-acento)" }}>
					<div className="wau-fila" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
						<div style={{ minWidth: 0, flex: "1 1 280px" }}>
							<h2 className="wau-h2">{fila?.horario || nombreHorario(sch.name)}, {sch.levelName || cursoCorto(sch.courseName)}</h2>
							<p className="wau-sec">{[sch.mentorName, sch.periodName, sch.startDate && sch.endDate ? `${fechaCorta(sch.startDate)} a ${fechaCorta(sch.endDate)}` : "", S.byStudent(rows.length)].filter(Boolean).join(", ")} {estadoPill}</p>
						</div>
						<div className="wau-fila">
							{sch.statusId === 1 || sch.statusId === 4 ? <button type="button" className="wau-btn s mini" disabled={busy} onClick={() => abrirModal("mentor")}>{S.changeMentor}</button> : null}
							{sch.statusId === 1 || sch.statusId === 4 ? <button type="button" className="wau-btn s mini" disabled={busy} onClick={() => abrirModal("merge")}>{S.merge}</button> : null}
							{sch.statusId === 1 ? <button type="button" className="wau-btn s mini" disabled={busy} onClick={() => abrirModal("close")}>{S.closeSchedule}</button> : null}
							{veWa && rows.length > 0 ? <button type="button" className="wau-btn s mini" disabled={busy} onClick={() => setMasivo(true)}>{S.whatsappVarias}</button> : null}
						</div>
					</div>

					<div className="wau-fila" style={{ alignItems: "center" }}>
						<div className="wau-seg" role="group">
							<button type="button" aria-pressed={seg === "alumnas"} onClick={() => setSeg("alumnas")}>{S.students}<span className="n">{rows.length}</span></button>
							<button type="button" aria-pressed={seg === "historial"} onClick={() => setSeg("historial")}>{S.historyTab}</button>
						</div>
						{porUbicar > 0 ? <span className="wau-pill warn">{S.toPlace(porUbicar)}</span> : null}
						{seg === "alumnas" ? (
							<div className="wau-chips" style={{ marginLeft: "auto", marginBottom: 0 }}>
								<FiltroCasillas etiqueta={S.col.pay} opciones={[{ v: "corriente", t: S.payOk }, { v: "vencida", t: "Vencida" }]} elegidos={pagoFiltro} onCambio={setPagoFiltro} />
								<FiltroCasillas etiqueta={S.col.attendance} opciones={[{ v: "normal", t: "Normal" }, { v: "baja", t: "Bajo el Mínimo" }]} elegidos={asistFiltro} onCambio={setAsistFiltro} />
							</div>
						) : null}
					</div>

					{seg === "historial" ? <HistorialLista campusId={campusId} countryId={countryId} rows={log} movil={movil} onChanged={(m) => notify(m)} onError={(m) => notify(m, false)} /> : vistas.length === 0 ? <div className="wau-sec">{S.empty}</div> : movil ? (
						<div>
							{vistas.map((r) => (
								<div key={"rc" + r.enrollmentId} className="wau-tarjeta" style={{ borderColor: selected.includes(r.enrollmentId) ? "var(--wa-acento)" : undefined }}>
									<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
										<input type="checkbox" checked={selected.includes(r.enrollmentId)} disabled={!libre(r)} onChange={() => toggle(r)} aria-label={r.studentName} />
										<b style={{ flex: 1 }}>{r.studentName}</b>{pagoPill(r)}
									</div>
									<div className="linea wau-num">{avance(r)}, {asistencia(r)}</div>
									<div className="linea">{ultimaTexto(r)}</div>
									<div style={{ marginTop: 6 }}><button type="button" className="wau-btn s mini" onClick={() => onActualizar(r.studentId, r.enrollmentId)}>{S.update}</button></div>
								</div>
							))}
						</div>
					) : (
						<div className="wau-tabla">
							<table>
								<thead><tr><th style={{ width: 1 }}><input type="checkbox" checked={todas} onChange={() => setSelected(todas ? [] : libres.map((r) => r.enrollmentId))} aria-label={S.selectAll} /></th>
									<th>{S.col.student}</th><th>{S.col.pay}</th><th>{S.col.progress}</th><th>{S.col.attendance}</th><th>{S.col.lastUpdate}</th><th /></tr></thead>
								<tbody className="wau-num">
									{vistas.map((r) => (
										<tr key={"r" + r.enrollmentId} className={selected.includes(r.enrollmentId) ? "sel" : ""}>
											<td><input type="checkbox" checked={selected.includes(r.enrollmentId)} disabled={!libre(r)} onChange={() => toggle(r)} aria-label={r.studentName} /></td>
											<td><b>{r.studentName}</b></td>
											<td>{pagoPill(r)}</td>
											<td>{avance(r)}</td>
											<td className={r.alerts.some((a) => a.code === "below_minimum") ? "wau-oro" : ""}>{asistencia(r)}</td>
											<td>{ultimaTexto(r)}</td>
											<td className="der"><button type="button" className="wau-btn s mini" onClick={() => onActualizar(r.studentId, r.enrollmentId)}>{S.update}</button></td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			) : null}

			{sch && personas.length > 0 ? (
				<div className="wau-caja wau-pila">
					<div className="wau-fila" style={{ justifyContent: "space-between" }}><h3>{S.moveStudents}</h3><span className="wau-pill flat">{fila?.horario || nombreHorario(sch.name)}, {sch.levelName}</span></div>
					<div className="wau-chips" role="group">
						{allowedTypes.map((id) => (
							<button key={"mv" + id} type="button" className="wau-chip" aria-pressed={typeId === id} onClick={() => { setTypeId(id); setDestino(null); setBuscandoDestino(true); }}>{TYPE_LABELS[id]}</button>
						))}
					</div>
					{necesitaDestino ? (
						<div className="wau-pila">
							<span className="wau-lab">{S.horarioDestino}</span>
							{destino && !buscandoDestino ? (
								<div className="wau-destino"><span><b>{destino.horario}</b>{destino.modulo ? ", " + destino.modulo : ""}{destino.maestro ? ", " + destino.maestro : ""}</span>
									<button type="button" className="wau-btn s mini" onClick={() => setBuscandoDestino(true)}>{S.cambiar}</button></div>
							) : (
								<BuscadorHorarios compacto campusId={campusId} soloIds={soloIds} complemento={complemento} detalle={detalle} seleccionado={destino?.id}
									onElegir={(h) => { setDestino(h); setBuscandoDestino(false); }} />
							)}
						</div>
					) : null}
					{typeId ? (
						<>
							{movil ? personas.map((p) => (
								<div key={"mp" + p.enrollmentId} className="wau-tarjeta">
									<div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><b>{p.studentName}</b>{pagoPill(p)}</div>
									<div className="linea wau-num">{avance(p)}</div>
									<input className="wau-entrada" style={{ marginTop: 6 }} placeholder={S.notePlaceholder} aria-label={S.note + " " + p.studentName} value={notes[p.enrollmentId] || ""}
										onChange={(e) => setNotes({ ...notes, [p.enrollmentId]: e.target.value })} />
								</div>
							)) : (
								<div className="wau-tabla">
									<table>
										<thead><tr><th>{S.col.student}</th><th>{S.col.pay}</th><th>{S.col.progress}</th>{necesitaDestino ? <th>{S.col.destination}</th> : null}<th>{S.col.note}</th></tr></thead>
										<tbody className="wau-num">
											{personas.map((p) => (
												<tr key={"mp" + p.enrollmentId}>
													<td><b>{p.studentName}</b></td><td>{pagoPill(p)}</td><td>{avance(p)}</td>
													{necesitaDestino ? <td>{destino?.horario || "—"}</td> : null}
													<td><input className="wau-entrada" placeholder={S.notePlaceholder} aria-label={S.note + " " + p.studentName} value={notes[p.enrollmentId] || ""}
														onChange={(e) => setNotes({ ...notes, [p.enrollmentId]: e.target.value })} /></td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
							{target && anyMore ? <div className="wau-nota"><span>{S.moreSessions(Math.max(...personas.map((p) => sessionDelta(p.sessionsTaken, target.classesGiven).more)))}</span></div> : null}
							<div className="wau-acciones" style={{ justifyContent: "flex-start", marginTop: 0 }}>
								{puedeAplicar ? <button type="button" className="wau-btn p" disabled={busy} onClick={() => mover(true)}>{S.applyN(personas.length)}</button> : null}
								<button type="button" className={"wau-btn " + (puedeAplicar ? "s" : "p")} disabled={!listo} onClick={() => mover(false)}>{S.leavePending}</button>
								{missing ? <span className="wau-oro" style={{ fontSize: 13.5 }}>{S.missingNote(missing.studentName.split(" ")[0])}</span> : null}
							</div>
						</>
					) : null}
				</div>
			) : null}

			{masivo && scheduleId && roster ? <EnvioMasivo campusId={campusId} studentIds={roster.rows.map((r) => r.studentId)} origen="horario" referencia={roster.schedule.name || null} onClose={() => setMasivo(false)} /> : null}

			{modal === "close" && scheduleId ? (
				<PromptModal title={S.closeSchedule} label={S.closeReason} confirm={S.closeSchedule} busy={busy} onCancel={() => setModal(null)}
					onConfirm={(t) => act(() => http.enrollmentUpdatesService.closeSchedule(campusId, scheduleId, t), S.closedOk)} />
			) : null}
			{modal === "merge" && scheduleId ? (
				<Modal titulo={S.merge} onClose={() => { setModal(null); setMergeTo(null); setMergeReason(""); }}>
					<span className="wau-lab">{S.mergeTarget}</span>
					<BuscadorHorarios compacto campusId={campusId} soloIds={soloIds.length ? soloIds : undefined} complemento={complemento} detalle={detalle} seleccionado={mergeTo?.id}
						onElegir={setMergeTo} />
					{mergeTo ? (
						<div className="wau-pila" style={{ marginTop: 10 }}>
							<div className="wau-destino"><span><b>{mergeTo.horario}</b>{mergeTo.modulo ? ", " + mergeTo.modulo : ""}{mergeTo.maestro ? ", " + mergeTo.maestro : ""}</span></div>
							<textarea className="wau-entrada" rows={2} placeholder={S.reason} aria-label={S.reason} value={mergeReason} onChange={(e) => setMergeReason(e.target.value)} />
							<div className="wau-acciones" style={{ justifyContent: "flex-start", marginTop: 0 }}>
								<button type="button" className="wau-btn p" disabled={busy}
									onClick={() => act(() => http.enrollmentUpdatesService.mergeSchedules(campusId, scheduleId, [scheduleId], mergeTo.id, mergeReason.trim() || undefined), S.mergedOk)}>{S.merge}</button>
							</div>
						</div>
					) : null}
				</Modal>
			) : null}
			{modal === "mentor" && scheduleId ? (
				<Modal titulo={S.changeMentor} onClose={() => setModal(null)}>
					<div className="wau-buscar"><input value={mentorFiltro} placeholder={S.newMentor} aria-label={S.newMentor} autoFocus onChange={(e) => setMentorFiltro(e.target.value)} /></div>
					<div className="wau-resultados" style={{ marginTop: 8 }}>
						{mentors === null ? <span className="wau-sec" style={{ padding: 10 }}>{S.loading}</span> : mentorLista.length === 0 ? <span className="wau-sec" style={{ padding: 10 }}>{S.empty}</span> : mentorLista.map((m) => (
							<button key={"me" + m.id} type="button" disabled={busy} onClick={() => act(() => http.enrollmentUpdatesService.changeMentor(campusId, scheduleId, m.id), S.mentorOk)}>
								<b>{m.name}</b><span className="wau-sec">{m.openSchedules}</span>
							</button>
						))}
					</div>
				</Modal>
			) : null}
		</div>
	);
};

export default UnHorario;
